import React, { useEffect, useRef, useState } from 'react';
import { Camera, ImagePlus, X, Loader2, Send } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import { easerGet, easerPost, errorMessage, compressImage, toISODate } from '../../lib/easerApi';
import { enqueue, shouldQueue, queuedBecause } from '../../lib/offlineQueue';

const VAT = [['20', '20 %'], ['10', '10 %'], ['5.5', '5,5 %'], ['0', '0 %']];
const MAX_RECEIPTS = 6;

/** New expense: type, date, amount, VAT, comment + scanned receipts. Sent to Dolibarr for approval. */
export function ExpenseDialog({ open, onClose, onDone, onUnauthorized }) {
  const [types, setTypes] = useState(null);
  const [typeCode, setTypeCode] = useState('');
  const [date, setDate] = useState(toISODate(new Date()));
  const [amount, setAmount] = useState('');
  const [vat, setVat] = useState('20');
  const [km, setKm] = useState('');
  const [comment, setComment] = useState('');
  const [photos, setPhotos] = useState([]);
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setTypeCode(''); setDate(toISODate(new Date())); setAmount(''); setVat('20'); setKm(''); setComment(''); setPhotos([]);
    if (types === null) {
      easerGet('/api/easer/expenses/types').then((res) => setTypes(res.data.items)).catch((err) => {
        if (err?.response?.status === 401) return onUnauthorized();
        setTypes([{ id: 0, code: 'TF_OTHER', label: 'Autre dépense' }]);
      });
    }
  }, [open, types, onUnauthorized]);

  const addFiles = async (files) => {
    const list = Array.from(files || []).slice(0, MAX_RECEIPTS - photos.length);
    if (!list.length) return;
    setAdding(true);
    try {
      const next = [];
      for (const f of list) {
        try { next.push({ kind: 'other', data: await compressImage(f) }); } catch { toast.error(`Photo illisible : ${f.name}`); }
      }
      setPhotos((p) => [...p, ...next]);
    } finally {
      setAdding(false);
    }
  };

  const isKm = typeCode === 'EX_KME';
  const amountNum = parseFloat(String(amount).replace(',', '.'));
  const valid = typeCode && date && amountNum > 0 && (isKm || photos.length > 0 || comment.trim());

  const submit = async () => {
    if (!typeCode) return toast.error('Choisissez le type de dépense');
    if (!(amountNum > 0)) return toast.error('Indiquez le montant TTC');
    if (!isKm && photos.length === 0 && !comment.trim()) return toast.error('Ajoutez la photo du ticket ou un commentaire');
    const payload = { date, type_code: typeCode, amount_ttc: Math.round(amountNum * 100) / 100, vat_rate: parseFloat(vat), comment, photos, km: isKm && km ? parseFloat(String(km).replace(',', '.')) : null };
    setSaving(true);
    try {
      const res = await easerPost('/api/easer/expenses', payload);
      if (res.data.submit_error) toast.warning(`Note créée (${res.data.ref}) mais non transmise automatiquement : ${res.data.submit_error}`, { duration: 8000 });
      else toast.success(`Note de frais ${res.data.ref} envoyée à l'agence`);
      onDone();
    } catch (err) {
      if (err?.response?.status === 401) return onUnauthorized();
      if (shouldQueue(err)) {
        const label = `${(types || []).find((t) => t.code === typeCode)?.label || 'Dépense'} · ${amountNum.toFixed(2)} €`;
        await enqueue({ type: 'expense', eventId: null, label, payload });
        toast.info(queuedBecause(err, 'note de frais'));
        return onDone();
      }
      toast.error(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !saving) onClose(); }}>
      <DialogContent className="max-w-md w-[95vw] max-h-[92vh] overflow-y-auto rounded-2xl" data-testid="expense-dialog">
        <DialogHeader className="text-left">
          <DialogTitle className="font-['Space_Grotesk']">Nouvelle note de frais</DialogTitle>
          <DialogDescription>Photographiez le ticket : la note part directement dans Dolibarr pour approbation par l'agence.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Type de dépense</Label>
            <Select value={typeCode} onValueChange={setTypeCode}>
              <SelectTrigger className="h-11" data-testid="expense-type"><SelectValue placeholder={types === null ? 'Chargement...' : 'Choisir...'} /></SelectTrigger>
              <SelectContent>
                {(types || []).map((t) => <SelectItem key={t.code} value={t.code} data-testid={`expense-type-${t.code}`}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="expense-date">Date</Label>
              <Input id="expense-date" type="date" value={date} max={toISODate(new Date())} onChange={(e) => setDate(e.target.value)} className="h-11" data-testid="expense-date" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="expense-amount">Montant TTC (€)</Label>
              <Input id="expense-amount" type="number" inputMode="decimal" step="0.01" min="0" placeholder="0,00" value={amount} onChange={(e) => setAmount(e.target.value)} className="h-11 text-lg font-semibold" data-testid="expense-amount" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>TVA</Label>
              <Select value={vat} onValueChange={setVat}>
                <SelectTrigger className="h-11" data-testid="expense-vat"><SelectValue /></SelectTrigger>
                <SelectContent>{VAT.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {isKm && (
              <div className="space-y-1.5">
                <Label htmlFor="expense-km">Kilomètres</Label>
                <Input id="expense-km" type="number" inputMode="decimal" step="0.1" min="0" placeholder="0" value={km} onChange={(e) => setKm(e.target.value)} className="h-11" data-testid="expense-km" />
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="expense-comment">Commentaire {isKm ? '(trajet)' : '(optionnel si ticket joint)'}</Label>
            <Textarea id="expense-comment" rows={2} value={comment} onChange={(e) => setComment(e.target.value)} placeholder={isKm ? 'Servins → Arras, client Dupont' : 'Plein pour la tournée du matin...'} data-testid="expense-comment" />
          </div>

          <div className="space-y-2">
            <Label>Tickets / justificatifs ({photos.length}/{MAX_RECEIPTS})</Label>
            <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} data-testid="expense-camera-input" />
            <input ref={galleryRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} data-testid="expense-gallery-input" />
            <div className="grid grid-cols-2 gap-2">
              <Button type="button" variant="outline" className="h-11" disabled={adding || photos.length >= MAX_RECEIPTS} onClick={() => cameraRef.current?.click()} data-testid="expense-take-photo">
                {adding ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Camera className="h-4 w-4 mr-1" />} Scanner le ticket
              </Button>
              <Button type="button" variant="outline" className="h-11" disabled={adding || photos.length >= MAX_RECEIPTS} onClick={() => galleryRef.current?.click()} data-testid="expense-pick-photo">
                <ImagePlus className="h-4 w-4 mr-1" /> Galerie
              </Button>
            </div>
            {photos.length > 0 && (
              <ul className="grid grid-cols-3 gap-2" data-testid="expense-photo-list">
                {photos.map((p, i) => (
                  <li key={i} className="relative rounded-lg border border-border overflow-hidden">
                    <img src={p.data} alt={`Ticket ${i + 1}`} className="h-24 w-full object-cover" />
                    <button type="button" onClick={() => setPhotos((l) => l.filter((_, idx) => idx !== i))} aria-label="Retirer" className="absolute top-1 right-1 h-7 w-7 rounded-full bg-black/60 text-white flex items-center justify-center" data-testid="expense-photo-remove"><X className="h-4 w-4" /></button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="expense-cancel">Annuler</Button>
          <Button onClick={submit} disabled={saving || adding || !valid} className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white" data-testid="expense-submit">
            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />} Envoyer à l'agence
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
